/**
 * Base API Client
 *
 * Core axios instance with `withCredentials: true` so the browser
 * automatically attaches the httpOnly auth cookies (admin_session,
 * portal_token, direct_user_token, marketing_session). No Bearer
 * header is set from the browser — those cookies are the single
 * source of authentication for browser clients.
 *
 * Non-browser callers (CLI / TUI / mobile / service-to-service)
 * continue to use `Authorization: Bearer` against the same endpoints;
 * the backend's unified-auth middleware accepts either transport.
 */

import axios, { AxiosInstance, AxiosError } from 'axios';

const CSRF_METHODS = new Set(['post', 'put', 'patch', 'delete']);

/**
 * Read the `csrf_token` cookie set by the backend at login. The backend
 * deliberately does NOT mark this cookie httpOnly so that this same-origin
 * read works (the paired auth cookie remains httpOnly). Returns null when
 * the user isn't logged in yet, in which case the request continues
 * without the header and the backend will either let it through
 * (unauthenticated route, e.g. /api/portal/auth/login) or reject it
 * (authenticated route — the missing cookie also means the missing CSRF
 * pair).
 */
export function getCsrfToken(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

class BaseApiClient {
  public client: AxiosInstance;

  // In-memory token cache. Populated from login responses so flows that
  // need the raw JWT in the same tab (cross-domain handoff, OAuth callback,
  // chat-stream EventSource) can still read it. NEVER persisted to
  // localStorage — the httpOnly cookie is the durable storage.
  private customerToken?: string;
  private serviceIntegratorToken?: string;
  private portalToken?: string;
  private enterpriseUserToken?: string;

  constructor(baseURL: string = '') {
    this.client = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true, // Send httpOnly auth cookies
    });

    // No request interceptor for Authorization: the browser already attaches
    // the auth cookie via `withCredentials: true`. Adding a Bearer header
    // here would defeat the XSS-mitigation point of moving to httpOnly cookies
    // (a stolen token in JS-reachable storage would let an attacker call the
    // API). CLI / mobile / service callers send Bearer themselves; for them
    // the backend's resolveAuth middleware reads the Authorization header.

    // CSRF interceptor (double-submit cookie pattern). Reads the
    // non-httpOnly `csrf_token` cookie set at login and echoes it back
    // in `X-CSRF-Token` on every state-changing request. The backend
    // verifies header === cookie (constant-time) before letting the
    // request through. Cross-origin attackers can't read the cookie
    // (Same-Origin Policy), so they can't forge the header — even though
    // the auth cookie would still ride along. See
    // backend/src/middleware/csrf.ts for the server-side check.
    this.client.interceptors.request.use(
      (config) => {
        const method = (config.method || '').toLowerCase();
        if (CSRF_METHODS.has(method)) {
          const csrf = getCsrfToken();
          if (csrf) {
            config.headers = config.headers || {};
            (config.headers as Record<string, string>)['X-CSRF-Token'] = csrf;
          }
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor - propagate axios errors unchanged so callers can
    // inspect `error.response.status` etc.
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError<{ error?: string; message?: string }>) => {
        return Promise.reject(error);
      }
    );
  }

  // ===== TOKEN MANAGEMENT =====

  /**
   * Returns whichever in-memory token was set during this session,
   * for the rare flows that need to forward the raw JWT (cross-domain
   * one-time-token exchange, OAuth callback handoff). After a page reload
   * this is `undefined` — that's intentional, the cookie handles auth.
   */
  getActiveToken(): string | undefined {
    return this.enterpriseUserToken
      || this.portalToken
      || this.customerToken
      || this.serviceIntegratorToken;
  }

  setCustomerToken(token: string) {
    this.customerToken = token;
    // Dispatch event so BrandingContext re-fetches with the new auth context
    window.dispatchEvent(new Event('customer-token-changed'));
  }

  setServiceIntegratorToken(token: string) {
    this.serviceIntegratorToken = token;
  }

  setPortalToken(token: string) {
    this.portalToken = token;
  }

  setDirectUserToken(token: string) {
    this.enterpriseUserToken = token;
  }

  clearAuth() {
    // Clear all in-memory tokens. The httpOnly cookies are cleared by the
    // backend logout endpoint(s); this just clears any cached copy held
    // by the API client.
    this.customerToken = undefined;
    this.serviceIntegratorToken = undefined;
    this.portalToken = undefined;
    this.enterpriseUserToken = undefined;

    // Dispatch event for components to respond (cache clearing, branding refresh, etc.)
    window.dispatchEvent(new CustomEvent('auth-cleared'));
  }

  /**
   * Clear in-memory auth for a specific token type only (used before setting
   * a new token). Ensures no stale token of the wrong type lingers in memory
   * when switching account types in the same tab.
   */
  clearOtherTokens(keepType: 'customer' | 'portal' | 'service_integrator' | 'direct_user') {
    if (keepType !== 'customer') this.customerToken = undefined;
    if (keepType !== 'portal') this.portalToken = undefined;
    if (keepType !== 'service_integrator') this.serviceIntegratorToken = undefined;
    if (keepType !== 'direct_user') this.enterpriseUserToken = undefined;
  }

  // ===== Legacy load* helpers =====
  // Pre-migration these read the JWT back from localStorage on app boot.
  // Cookies are httpOnly so the JS can't see them — instead, app boot
  // detects the session by calling /api/portal/auth/me and trusting the
  // response. These helpers are kept as in-memory checks so callers
  // (AuthContext, impersonation flow) compile unchanged; the real session
  // detection is via the /me round-trip.

  loadCustomerToken(): boolean {
    return !!this.customerToken;
  }

  loadServiceIntegratorToken(): boolean {
    return !!this.serviceIntegratorToken;
  }

  loadPortalToken(): boolean {
    return !!this.portalToken;
  }

  loadDirectUserToken(): boolean {
    return !!this.enterpriseUserToken;
  }

  getPortalToken(): string | undefined {
    return this.portalToken;
  }

  getDirectUserToken(): string | undefined {
    return this.enterpriseUserToken;
  }

  getCustomerToken(): string | undefined {
    return this.customerToken;
  }

  getServiceIntegratorToken(): string | undefined {
    return this.serviceIntegratorToken;
  }
}

// Singleton instance
export const baseClient = new BaseApiClient();

// Export the axios instance for direct use
export const http = baseClient.client;

// Export the class for type annotations
export type { BaseApiClient };
